import { User, PencilSimple, ArrowSquareOut, Plus } from '@phosphor-icons/react'
import { useAccountPageUIContext } from '../AccountPageProvider'
import { hexToRgba } from '../../utils/colorUtils'
import { AccountPageExpandedSection } from '../types'
import type { BorderSides } from '../types'
import ProfileLogo from '../utility-components/ProfileLogo'

const getBorderStyle = (sides: BorderSides, width: string, color: string) => {
  if (sides === 'none') return {}
  if (sides === 'bottom') return { borderBottomWidth: width, borderBottomColor: color, borderBottomStyle: 'solid' as const }
  if (sides === 'top') return { borderTopWidth: width, borderTopColor: color, borderTopStyle: 'solid' as const }
  return { borderWidth: width, borderColor: color, borderStyle: 'solid' as const }
}

const getInitials = (name: string) =>
  name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()

const getFirstName = (name: string) => name.split(' ')[0]

const ProfileFieldsList = () => {
  const { uiConfig: config, data, actions } = useAccountPageUIContext()
  const { details, button } = config.pages.profile
  const profilePage = data?.profilePage
  const user = profilePage?.user
  const isLoading = profilePage?.isLoading ?? false
  const fields = [
    { label: 'Name', value: isLoading ? '...' : (user?.name ?? '—'), manageable: false },
    { label: 'Phone Number', value: isLoading ? '...' : (user?.phone ?? '—'), manageable: false },
    { label: 'Email', value: isLoading ? '...' : (user?.primaryEmail ?? ''), manageable: true },
  ]

  return (
    <div className='ui-flex ui-flex-col ui-py-[8px]'>
      {fields.map(({ label, value, manageable }, index) => (
        <div
          key={label}
          className={`ui-flex ui-items-center ui-justify-between ui-px-3 ui-py-3 @[768px]:ui-px-[16px] @[768px]:ui-py-[10px]${index < fields.length - 1 ? ' ui-border-b ui-border-solid' : ''}`}
          style={{ borderColor: details.border_color || 'rgba(0,0,0,0.06)' }}
        >
          <span
            className='ui-text-text-sm-medium'
            style={{ color: hexToRgba(details.text_color, 0.6) }}
          >
            {label}
          </span>
          <div className='ui-flex ui-items-center ui-gap-2'>
            <span className='ui-text-text-sm-medium' style={{ color: details.text_color }}>
              {value}
            </span>
            {manageable && !value && (
              <button
                className='ui-flex ui-items-center ui-gap-1 ui-text-[11px] ui-font-medium ui-px-2 @[768px]:ui-px-3 ui-py-1 ui-rounded-md'
                style={{
                  color: button.text_color,
                  backgroundColor: button.background_color,
                }}
                onClick={e => { e.stopPropagation(); actions.onOpenEmailFlow?.({ returnToOrders: false }) }}
              >
                <Plus size={12} weight='bold' />
                Add Email
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const ProfilePageUI = () => {
  const { uiConfig: config, data, actions } = useAccountPageUIContext()
  const { details, button } = config.pages.profile
  const user = data?.profilePage?.user

  const initials = user?.name ? getInitials(user.name) : 'AU'
  const firstName = user?.name ? getFirstName(user.name) : 'there'

  return (
    <div
      className='ui-flex ui-flex-col ui-w-full ui-px-4 @[768px]:ui-items-center @[768px]:ui-justify-center @[768px]:ui-px-[40px]'
      onClick={() => actions.onSectionClick?.(AccountPageExpandedSection.PROFILE, 'HEADER')}
    >
      <div className='ui-min-h-[600px] ui-pt-4 @[768px]:ui-pt-[48px] @[768px]:ui-items-center @[768px]:ui-justify-center'>
        {/* Avatar + greeting — hidden on desktop, shown in sidebar instead */}
        <div className='@[768px]:ui-hidden'>
          <div className='ui-flex ui-items-center ui-gap-2'>
            <User size={22} weight='fill' color={hexToRgba(details.text_color, 0.7)} />
            <span className='ui-text-text-xl-semibold' style={{ color: details.text_color }}>
              Profile
            </span>
          </div>
          <div className='ui-flex ui-flex-col ui-items-center ui-justify-center'>
            <ProfileLogo size={80} initials={initials} />
            <div className='ui-pt-3 ui-pb-6' style={{ color: details.text_color }}>
              <p className='ui-text-text-md-regular'>Hi <span className='ui-text-text-md-semibold'>{firstName}</span></p>
            </div>
          </div>
        </div>

        {/* Details card */}
        <div
          className='ui-flex ui-flex-col ui-w-full @[768px]:ui-w-[540px] @[768px]:ui-p-[16px] @[768px]:ui-shadow-[0px_0px_0px_1px_rgba(10,13,18,0.05),0px_1px_2px_0px_rgba(10,13,18,0.05)]'
          style={{
            backgroundColor: details.background_color,
            borderRadius: details.card_radius,
            ...getBorderStyle(details.sides, details.card_border, details.border_color)
          }}
        >
          {/* "Details" heading — desktop only */}
          <div className='ui-hidden @[768px]:ui-block ui-px-[16px] ui-pb-[4px]'>
            <p
              className='ui-text-[18px] ui-font-semibold ui-leading-[28px] ui-tracking-[-0.18px]'
              style={{ color: details.text_color }}
            >
              Details
            </p>
          </div>

          <ProfileFieldsList />

          <button
            className='ui-w-full ui-flex ui-items-center ui-overflow-hidden ui-px-4 ui-py-3 ui-mt-6 @[768px]:ui-px-[16px] @[768px]:ui-py-[12px] @[768px]:ui-mt-8 @[768px]:ui-shadow-[0px_0px_0px_1px_rgba(10,13,18,0.05),0px_1px_2px_0px_rgba(10,13,18,0.05)]'
            style={{ backgroundColor: button.background_color, borderRadius: button.border_radius }}
            onClick={e => { e.stopPropagation(); actions?.onManageDetails?.(); actions.onSectionClick?.(AccountPageExpandedSection.PROFILE, 'TOP_BANNER') }}
          >
            <div className='ui-flex ui-flex-1 ui-items-center ui-gap-3 @[768px]:ui-gap-[12px] ui-min-w-0'>
              <div className='ui-flex ui-items-center ui-justify-center ui-shrink-0 ui-opacity-60'>
                <PencilSimple size={16} color={button.text_color} weight='regular' />
              </div>
              <div className='ui-flex ui-flex-col ui-flex-1 ui-min-w-0 ui-items-start ui-justify-center'>
                <span
                  className='ui-text-[14px] ui-font-medium ui-leading-[22px] ui-tracking-[-0.14px] ui-whitespace-nowrap'
                  style={{ color: button.text_color }}
                >
                  {button.cta_label}
                </span>
                <span
                  className='ui-text-[12px] ui-font-medium ui-leading-[18px] ui-tracking-[-0.12px] ui-opacity-50'
                  style={{ color: button.text_color }}
                >
                  You'll be redirect to shopflo accounts to manage your details
                </span>
              </div>
            </div>
            <div className='ui-flex ui-items-center ui-justify-center ui-shrink-0 ui-opacity-60'>
              <ArrowSquareOut size={16} color={button.text_color} weight='regular' />
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProfilePageUI
